import { useQuery, useQueryClient } from '@tanstack/react-query'
import { RRule } from 'rrule'
import { parseISO, addDays } from 'date-fns'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'
import { saveWithConflictCheck } from '../../../lib/conflictResolver'

export const useTasks = () => {
  const { household, user } = useAuth()
  const qc = useQueryClient()
  const key = ['tasks', household?.id]

  const query = useQuery({
    queryKey: key,
    enabled: !!household?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tasks')
        .select('id, household_id, title, description, assigned_to, due_date, completed_at, completed_by, recurrence_rule, recurrence_end, event_id, created_by, created_at')
        .eq('household_id', household.id)
        .order('due_date', { ascending: true, nullsFirst: false })
      if (error) throw error
      return data ?? []
    },
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: key })

  const createTask = async (fields) => {
    const { error } = await supabase
      .from('tasks')
      .insert({ ...fields, household_id: household.id, created_by: user.id })
    if (error) throw error
    invalidate()
  }

  const updateTask = async ({ id, localUpdatedAt, ...fields }) => {
    if (localUpdatedAt) {
      const result = await saveWithConflictCheck('tasks', id, fields, localUpdatedAt)
      if (result.conflict) return result
    } else {
      const { error } = await supabase.from('tasks').update(fields).eq('id', id)
      if (error) throw error
    }
    invalidate()
    return {}
  }

  const completeTask = async (task) => {
    const { error } = await supabase
      .from('tasks')
      .update({ completed_at: new Date().toISOString(), completed_by: user.id })
      .eq('id', task.id)
    if (error) throw error

    if (task.recurrence_rule) {
      try {
        const now   = new Date()
        const base  = task.due_date ? parseISO(task.due_date) : now
        const rule  = RRule.fromString(
          `DTSTART:${base.toISOString().replace(/[-:]/g, '').split('.')[0]}Z\nRRULE:${task.recurrence_rule}`,
        )
        const next  = rule.after(now)
        const until = task.recurrence_end ? parseISO(task.recurrence_end) : null
        if (next && (!until || next <= until)) {
          await supabase.from('tasks').insert({
            household_id:    task.household_id,
            title:           task.title,
            description:     task.description,
            assigned_to:     task.assigned_to,
            due_date:        next.toISOString(),
            recurrence_rule: task.recurrence_rule,
            recurrence_end:  task.recurrence_end,
            event_id:        task.event_id,
            created_by:      task.created_by,
          })
        }
      } catch { /* best-effort */ }
    }
    invalidate()
  }

  const uncompleteTask = async (id) => {
    const { error } = await supabase
      .from('tasks')
      .update({ completed_at: null, completed_by: null })
      .eq('id', id)
    if (error) throw error
    invalidate()
  }

  const deleteTask = async (id) => {
    const { error } = await supabase.from('tasks').delete().eq('id', id)
    if (error) throw error
    invalidate()
  }

  return { ...query, createTask, updateTask, completeTask, uncompleteTask, deleteTask }
}
